/**
 * VerificationContext — email verification gate. Runs BEFORE onboarding.
 * A user with `email_verification_required=true` and `email_verified=false`
 * is redirected to /verify and blocked from every other authenticated
 * route until they enter the code emailed to them.
 *
 * Legacy actives are grandfathered on the backend (backfill runs at
 * startup) so this context is effectively a no-op for them.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VerificationContext = createContext({
  loading: true,
  required: false,
  verified: false,
  locked: false,
  emailMasked: '',
  refresh: async () => {},
});

export function VerificationProvider({ children }) {
  const { user } = useAuth();
  const [state, setState] = useState({
    loading: true,
    required: false,
    verified: false,
    emailMasked: '',
    verifiedAt: null,
    codeTtlMinutes: 10,
    maxAttempts: 5,
    maxResendsPerHour: 5,
  });

  const fetchStatus = useCallback(async () => {
    // Only employees + supervisors are ever gated. Admins pass through.
    if (!user || (user.role !== 'employee' && user.role !== 'supervisor')) {
      setState((s) => ({
        ...s,
        loading: false,
        required: false,
        verified: true,
      }));
      return;
    }
    try {
      const { data } = await axios.get(`${API}/verification/status`);
      setState({
        loading: false,
        required: !!data.email_verification_required,
        verified: !!data.email_verified,
        emailMasked: data.email_masked || '',
        verifiedAt: data.email_verified_at || null,
        codeTtlMinutes: data.code_ttl_minutes || 10,
        maxAttempts: data.max_attempts || 5,
        maxResendsPerHour: data.max_resends_per_hour || 5,
      });
    } catch (err) {
      // Fail open — don't lock the user out if the endpoint is
      // temporarily unreachable. Backend is the source of truth.
      console.warn('Verification status fetch failed:', err);
      setState((s) => ({
        ...s,
        loading: false,
        required: false,
        verified: true,
      }));
    }
  }, [user]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const value = useMemo(
    () => ({
      loading: state.loading,
      required: state.required,
      verified: state.verified,
      emailMasked: state.emailMasked,
      verifiedAt: state.verifiedAt,
      codeTtlMinutes: state.codeTtlMinutes,
      maxAttempts: state.maxAttempts,
      maxResendsPerHour: state.maxResendsPerHour,
      // "locked" = the gate is active right now. All routes except
      // /verify should redirect here while true.
      locked: state.required && !state.verified,
      refresh: fetchStatus,
    }),
    [state, fetchStatus],
  );

  return (
    <VerificationContext.Provider value={value}>
      {children}
    </VerificationContext.Provider>
  );
}

export function useVerification() {
  return useContext(VerificationContext);
}
